import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { VideoRecord } from "../lib/types";
import type { VideoSeed } from "./Portal";

const ASPECTS = ["16:9", "9:16", "1:1"];
const DURATIONS = [5, 10];

export default function VideoTab({
  seed,
  onClearSeed,
  onExtend,
  onError,
}: {
  seed: VideoSeed | null;
  onClearSeed: () => void;
  onExtend: (rec: VideoRecord) => void;
  onError: (e: unknown) => void;
}) {
  const [prompt, setPrompt] = useState(seed?.prompt ?? "");
  const [aspect, setAspect] = useState("16:9");
  const [duration, setDuration] = useState(5);
  const [busy, setBusy] = useState(false);
  const [videos, setVideos] = useState<VideoRecord[]>([]);
  const pollers = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (seed?.prompt) setPrompt(seed.prompt);
  }, [seed]);

  useEffect(() => {
    api.history().then((h) => setVideos(h.videos.slice(0, 20))).catch(onError);
    return () => {
      pollers.current.forEach((id) => window.clearInterval(id));
      pollers.current.clear();
    };
  }, [onError]);

  function poll(id: string) {
    if (pollers.current.has(id)) return;
    const interval = window.setInterval(async () => {
      try {
        const v = await api.getVideo(id);
        setVideos((list) => list.map((x) => (x.id === id ? v : x)));
        if (v.status === "completed" || v.status === "failed") {
          window.clearInterval(interval);
          pollers.current.delete(id);
        }
      } catch (e) {
        window.clearInterval(interval);
        pollers.current.delete(id);
        onError(e);
      }
    }, 4000);
    pollers.current.set(id, interval);
  }

  async function submit() {
    const text = prompt.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      let body: Parameters<typeof api.submitVideo>[0];
      if (seed?.kind === "image" && seed.sourceImageId) {
        body = { kind: "image", prompt: text, source_image_id: seed.sourceImageId, duration, aspect_ratio: aspect };
      } else if (seed?.kind === "extend" && seed.sourceVideoId) {
        body = { kind: "extend", prompt: text, source_video_id: seed.sourceVideoId, duration };
      } else {
        body = { kind: "text", prompt: text, duration, aspect_ratio: aspect };
      }
      const { id } = await api.submitVideo(body);
      const pending = await api.getVideo(id);
      setVideos((v) => [pending, ...v]);
      poll(id);
      onClearSeed();
      setPrompt("");
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  const seedLabel =
    seed?.kind === "image"
      ? "Animating an image"
      : seed?.kind === "extend"
      ? "Extending a video"
      : "Text → video";

  return (
    <div className="h-full flex flex-col p-4 gap-4 overflow-y-auto">
      <div className="card p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs">
          <span className="chip">{seedLabel}</span>
          {seed?.sourceImageUrl && (
            <img src={seed.sourceImageUrl} className="h-12 w-12 rounded object-cover ring-1 ring-zinc-800" />
          )}
          {seed?.sourceVideoUrl && (
            <video src={seed.sourceVideoUrl} className="h-12 w-12 rounded object-cover ring-1 ring-zinc-800" muted />
          )}
          {seed && (
            <button className="btn-ghost text-xs ml-auto" onClick={onClearSeed}>
              Clear source
            </button>
          )}
        </div>

        <label className="text-xs text-zinc-400">Prompt</label>
        <textarea
          className="input resize-none"
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            seed?.kind === "image"
              ? "How should this image move? Camera, motion, ambience…"
              : seed?.kind === "extend"
              ? "Continue with… (motion, what happens next)"
              : "A cinematic 5-second clip of…"
          }
        />
        <div className="flex items-center gap-2 flex-wrap">
          {seed?.kind !== "extend" && (
            <>
              <span className="text-xs text-zinc-400 mr-1">Aspect</span>
              {ASPECTS.map((a) => (
                <button
                  key={a}
                  className={`chip ${aspect === a ? "ring-1 ring-violet-500 text-white" : ""}`}
                  onClick={() => setAspect(a)}
                >
                  {a}
                </button>
              ))}
            </>
          )}
          <span className="text-xs text-zinc-400 mr-1 ml-3">Duration</span>
          {DURATIONS.map((d) => (
            <button
              key={d}
              className={`chip ${duration === d ? "ring-1 ring-violet-500 text-white" : ""}`}
              onClick={() => setDuration(d)}
            >
              {d}s
            </button>
          ))}
          <button className="btn-primary ml-auto" onClick={submit} disabled={busy || !prompt.trim()}>
            {busy ? "Submitting…" : "Generate video"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {videos.map((v) => (
          <VideoCard key={v.id} v={v} onExtend={onExtend} onMount={() => v.status !== "completed" && v.status !== "failed" && poll(v.id)} />
        ))}
        {videos.length === 0 && (
          <div className="text-zinc-500 text-sm">No videos yet. Submit one above.</div>
        )}
      </div>
    </div>
  );
}

function VideoCard({
  v,
  onExtend,
  onMount,
}: {
  v: VideoRecord;
  onExtend: (rec: VideoRecord) => void;
  onMount: () => void;
}) {
  useEffect(() => {
    onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="card overflow-hidden flex flex-col">
      <div className="aspect-video bg-zinc-950 flex items-center justify-center">
        {v.status === "completed" && v.url ? (
          <video src={v.url} controls className="w-full h-full object-contain" />
        ) : v.status === "failed" ? (
          <span className="text-rose-400 text-xs p-3">Failed: {v.error ?? "unknown"}</span>
        ) : (
          <span className="text-zinc-500 text-xs">⏳ {v.status}…</span>
        )}
      </div>
      <div className="p-3 text-xs text-zinc-400 flex flex-col gap-2 flex-1">
        <div className="flex gap-2 flex-wrap">
          <span className="chip">{v.kind}</span>
          {v.duration && <span className="chip">{v.duration}s</span>}
          {v.aspect_ratio && <span className="chip">{v.aspect_ratio}</span>}
        </div>
        <div className="line-clamp-3 text-zinc-300">{v.prompt}</div>
        <div className="flex gap-2 mt-auto">
          <button
            className="btn-secondary text-xs"
            onClick={() => onExtend(v)}
            disabled={v.status !== "completed"}
          >
            Extend
          </button>
          {v.url && (
            <a className="btn-ghost text-xs" href={v.url} target="_blank" rel="noreferrer">
              Open
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
